import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip,
    TextField, Stack, IconButton, Tooltip,
    useTheme, useMediaQuery, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import {
    History as HistoryIcon,
    Search as SearchIcon,
    Public as PublicIcon,
    Person as PersonIcon,
    DeleteOutline as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const AuditLogsPage = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [user, setUser] = useState<any>(null);
    const [tenants, setTenants] = useState<any[]>([]);
    const [selectedTenant, setSelectedTenant] = useState('all');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const fetchLogs = useCallback(async () => {
        try {
            setLoading(true);
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            const params: any = {};
            if (selectedTenant && selectedTenant !== 'all') {
                params.filterTenantId = selectedTenant;
            }
            const res = await axios.get(getApiUrl('audit'), {
                headers: { Authorization: `Bearer ${userInfo.token}` },
                params
            });
            setLogs(res.data);
            setUser(userInfo.user || userInfo);
        } catch (error) {
            console.error('Error fetching audit logs:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedTenant]);

    const fetchTenants = useCallback(async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            if (userInfo.user?.role === 'SAAS_ADMIN' || userInfo?.role === 'SAAS_ADMIN') {
                const res = await axios.get(getApiUrl('saas-admin/tenants'), {
                    headers: { Authorization: `Bearer ${userInfo.token}` }
                });
                setTenants(res.data);
            }
        } catch (error) {
            console.error('Error fetching tenants:', error);
        }
    }, []);

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this log entry? This action itself will be logged.')) return;

        try {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            await axios.delete(getApiUrl(`audit/${id}`), {
                headers: { Authorization: `Bearer ${userInfo.token}` }
            });
            fetchLogs();
        } catch (error: any) {
            alert(error.response?.data?.message || 'Deletion failed. Only SuperAdmin can delete logs.');
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    useEffect(() => {
        fetchTenants();
    }, [fetchTenants]);

    const filteredLogs = logs.filter((log: any) =>
        log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getActionColor = (action: string) => {
        if (action.includes('SUCCESS')) return '#E6FFFA'; // Greenish
        if (action.includes('FAILURE')) return '#FFF5F5'; // Reddish
        if (action.includes('DELETE')) return '#FFF5F5';
        if (action.includes('SUDO')) return '#FEEBC8'; // Orange
        return '#E2E8F0'; // Grey
    };

    const getActionTextColor = (action: string) => {
        if (action.includes('SUCCESS')) return '#38A169';
        if (action.includes('FAILURE')) return '#E53E3E';
        if (action.includes('DELETE')) return '#E53E3E';
        if (action.includes('SUDO')) return '#C05621';
        return '#4A5568';
    };

    return (
        <Box sx={{ p: isMobile ? 2 : 4, bgcolor: '#fbfbfb', minHeight: '100vh' }}>
            <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} mb={4} spacing={2}>
                <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight={900} color="#1a237e" gutterBottom>
                        System Audit Logs
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Secure, immutable records of all critical system activities.
                    </Typography>
                </Box>
                <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems="center" width={isMobile ? '100%' : 'auto'}>
                    {user?.role === 'SAAS_ADMIN' && (
                        <FormControl size="small" sx={{ minWidth: 200, bgcolor: 'white', borderRadius: '12px' }}>
                            <InputLabel id="tenant-filter-label">Filter by School</InputLabel>
                            <Select
                                labelId="tenant-filter-label"
                                value={selectedTenant}
                                label="Filter by School"
                                onChange={(e) => setSelectedTenant(e.target.value)}
                                sx={{ borderRadius: '12px' }}
                            >
                                <MenuItem value="all">All Schools</MenuItem>
                                {tenants.map((t) => (
                                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    <Paper
                        elevation={0}
                        sx={{
                            p: '4px 12px',
                            display: 'flex',
                            alignItems: 'center',
                            width: isMobile ? '100%' : 300,
                            bgcolor: 'white',
                            borderRadius: '16px',
                            border: '1px solid #eee'
                        }}
                    >
                        <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                        <TextField
                            fullWidth
                            variant="standard"
                            placeholder="Search logs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{ disableUnderline: true }}
                        />
                    </Paper>
                </Stack>
            </Stack>

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: isMobile ? '12px' : '24px', border: '1px solid #eee', overflow: 'hidden' }}>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: isMobile ? 800 : 'auto' }}>
                        <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 800 }}>Timestamp</TableCell>
                                {user?.role === 'SAAS_ADMIN' && (
                                    <TableCell sx={{ fontWeight: 800 }}>School</TableCell>
                                )}
                                <TableCell sx={{ fontWeight: 800 }}>User</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Action</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>IP Address</TableCell>
                                <TableCell sx={{ fontWeight: 800 }} align="right">Manage</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredLogs.map((log: any) => (
                                <TableRow key={log.id} hover>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                            <Typography variant="body2" fontWeight={500}>
                                                {new Date(log.createdAt).toLocaleString()}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                            <Typography variant="body2" fontWeight={600}>
                                                {log.userName}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    {user?.role === 'SAAS_ADMIN' && (
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={700} color="#1a237e">
                                                {log.tenant?.name || 'System'}
                                            </Typography>
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <Chip
                                            label={log.action}
                                            size="small"
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: '0.65rem',
                                                bgcolor: getActionColor(log.action),
                                                color: getActionTextColor(log.action),
                                                border: '1px solid rgba(0,0,0,0.05)'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ minWidth: 300, color: 'text.primary' }}>
                                            {log.description}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <PublicIcon sx={{ fontSize: 16, color: '#1a237e' }} />
                                            <Typography variant="caption" fontWeight={700} color="#1a237e">
                                                {log.ipAddress}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Delete">
                                            <IconButton size="small" onClick={() => handleDelete(log.id)} sx={{ color: '#E53E3E' }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </TableContainer>

            {filteredLogs.length === 0 && !loading && (
                <Box textAlign="center" py={10}>
                    <Typography color="text.secondary">No audit logs found matching your criteria.</Typography>
                </Box>
            )}
        </Box>
    );
};

export default AuditLogsPage;
